import axios from 'axios';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  updateDoc,
  doc,
  writeBatch,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebase';

export const SYSTEM_EMAILS = {
  ADMIN: 'admin@pambo.biz',
  SUPPORT: 'support@pambo.biz',
  INFO: 'info@pambo.biz',
  PRIVACY: 'privacy@pambo.biz',
  FOUNDER: 'founder@pambo.biz',
};

export interface Notification {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'order' | 'message' | 'review';
  read: boolean;
  link?: string;
  createdAt: any;
}

export const notificationService = {
  sendEmail: async (to: string, subject: string, text: string, html?: string) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User must be logged in to trigger notifications');
      }

      const token = await user.getIdToken();

      const response = await axios.post(
        '/api/notifications/email',
        {
          to,
          subject,
          text,
          html,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error sending email notification:', error);
      throw error;
    }
  },

  sendPush: async (userId: string, title: string, body: string, data?: any) => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const token = await user.getIdToken();

      await axios.post(
        '/api/notifications/push',
        {
          userId,
          title,
          body,
          data,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      console.error('Error sending push notification:', error);
    }
  },

  // In-app notifications
  sendInApp: async (
    userId: string,
    title: string,
    message: string,
    type: Notification['type'] = 'info',
    link?: string
  ) => {
    try {
      await addDoc(collection(db, 'notifications'), {
        userId,
        title,
        message,
        type,
        link,
        read: false,
        createdAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error sending in-app notification:', error);
    }
  },

  getNotifications: (userId: string, callback: (notifications: Notification[]) => void) => {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const notifications = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as Notification
        );
        callback(notifications);
      },
      (error) => {
        console.error('Error fetching notifications:', error);
      }
    );
  },

  markAsRead: async (notificationId: string) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true,
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  },

  markAllAsRead: async (userId: string) => {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false)
      );
      const snapshot = await getDocs(q); // Need getDocs here for batch
      const batch = writeBatch(db);
      snapshot.docs.forEach((d) => {
        batch.update(d.ref, { read: true });
      });
      await batch.commit();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  },
};
